<template>
    <div>
        <v-autocomplete
            v-model="itemId"
            :items="items"
            :label="`Sublocal`"
            :item-value="'id'"
            :item-text="'text'"
            persistent-hint
            @change="inputChange"
            @keyup.enter="inputEnter"
            @keyup.delete="inputDelete"
            @keydown.tab="inputTab"
            @focus="inputFocus"
            @blur="inputBlur"
            :loading="loading"
            :disabled="disabled"
            :rules="rulesRequired"
        >
        </v-autocomplete>
    </div>
</template>

<script>
import { showErrorSnackBar } from "@/utils/SnackBartHelper"
import Rules from "@/utils/Rules";
import Utils from '@/scripts/Utils';

export default {
    name: "SublocalField",
    props: {
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        proposal: {
            required: true,
        },
        id: {
            type: String,
            default: '',
        },
        noRule: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        items: [],
        itemId: null,
        item: null,
        focus: false,
        pendingNewItem: null, // Armazena item criado pelo usuário que ainda não está na lista
    }),
    methods: {
        inputTab(e) {
            this.inputEnter(e)
        },
        inputEnter(e) {
            const value = e.target.value.trim();

            if (value.length === 0) {
                this.itemId = null;
                return;
            }
            this.emitLoading(true)
            this.emitDisabled(true)
            let valueId = Utils.createSlug(value)
            this.items.push({
                text: value,
                id: valueId
            })
            this.itemId = valueId
            let sublocals = Utils.sortArray(this.items.map(({text}) => text))
            let items = sublocals.map(
                (sublocal) => {
                    return {
                        text: sublocal,
                        id: Utils.createSlug(sublocal)
                    }
                }
            )
            this.items = items
            this.itemId = valueId
            
            // Emite o input imediatamente para atualizar o valor no componente pai
            let newItem = { text: value, id: valueId }
            this.pendingNewItem = newItem // Armazena para preservar se o watch do proposal resetar
            this.$emit('change:input', newItem)
            
            this.$nextTick(
                () => {
                    document.querySelectorAll("div.v-list[role='listbox'] div div div").forEach(
                        (el) => {
                            let content = el.innerHTML
                            if (content === value) {
                                el.click()
                            }
                        }
                    )
                }
            )
            this.emitLoading(false)
            this.emitDisabled(false)
        },
        inputDelete(e) {
            const { key } = e
            if (key !== 'Delete') {
                this.inputFocus()
                return
            }
            this.emitLoading(true)
            this.emitDisabled(true)
            // Para Diagnostic, verifica se há soluções usando este sub-local
            let itemIds = !this.proposal.solutionList ? [] : this.proposal.solutionList
                .filter(item => item.isDeleted !== true)
                .map(({sub_local}) => Utils.createSlug(sub_local))
            let hasDependency = itemIds.includes(this.itemId)
            if (hasDependency) {
                showErrorSnackBar("Não pode deletar o sub local pois há solução no checklist")
                this.emitLoading(false)
                this.emitDisabled(false)
                return
            }
            this.items = this.items.filter(({id}) => id !== this.itemId)
            this.itemId = null
            this.emitLoading(false)
            this.emitDisabled(false)
            this.inputFocus()
        },
        inputChange() {
            this.emitInput()
        },
        inputFocus() {
            this.focus = true
            this.emitFocus(this.focus)
        },
        inputBlur(e) {
            this.focus = false
            this.emitFocus(this.focus)

            const value = e.target.value ? e.target.value.trim() : ''
            const valueId = Utils.createSlug(value)

            // Valor digitado já é o selecionado — nada a fazer
            if (this.itemId === valueId) return
            // itemId foi atualizado pelo @change (seleção do dropdown)
            // mas o input ainda mostra o texto antigo — não sobrescreve
            const selectedItem = this.items.find(({ id }) => id === this.itemId)

            if (selectedItem) return
            this.inputEnter(e)
        },
        emitLoading(status) {
            this.$emit('loading:input', status)
        },
        emitDisabled(status) {
            this.$emit('disabled:input', status)
        },
        emitFocus(status) {
            this.$emit('focus:input', status)
        },
        emitInput() {
            let item = this.items.find(({id}) => id === this.itemId)
            if (item) {
                this.pendingNewItem = null // Limpa pendente quando item é selecionado da lista
            }
            this.$emit('change:input', item)
            this.emitLoading(false)
            this.emitDisabled(false)
            this.emitFocus(false)
        }
    },
    computed: {
        rulesRequired() {
            if (this.noRule) return []
            return Rules.required();
        },
    },
    watch: {
        proposal: {
            immediate: true,
            handler: function (newVal) {
                if (newVal) {
                    let items = []
                    let solutionList = newVal.solutionList || newVal
                    if (!solutionList || !Array.isArray(solutionList)) {
                        // Mantém items existentes se houver itemId selecionado
                        if (this.itemId && this.items.length > 0) {
                            return
                        }
                        this.items = []
                        return
                    }
                    // Filtra apenas soluções não deletadas
                    let validSolutions = solutionList.filter(item => item.isDeleted !== true)
                    let sublocals = Utils.sortArray(Utils.removeDuplicates(validSolutions.map(({ sub_local }) => sub_local).filter(sub_local => sub_local)))
                    items = sublocals.map((sublocal) => ({
                        text: sublocal,
                        id: Utils.createSlug(sublocal)
                    }))
                    
                    // Preserva o item atual se ele não estiver na lista
                    if (this.itemId) {
                        let currentItem = this.items.find(item => item.id === this.itemId)
                        if (currentItem && !items.find(item => item.id === this.itemId)) {
                            items.push(currentItem)
                        }
                    }
                    
                    // Preserva item pendente (criado pelo usuário)
                    if (this.pendingNewItem && !items.find(item => item.id === this.pendingNewItem.id)) {
                        items.push(this.pendingNewItem)
                    }
                    
                    // Ordena os items
                    items = Utils.sortArray(items.map(item => item.text)).map(text => {
                        return items.find(item => item.text === text)
                    }).filter(item => item !== undefined)
                    
                    this.items = items
                }
            }
        },
        id: {
            immediate: true,
            handler: function (newVal) {
                if (newVal) {
                    // Se o valor já existe nos items, usa o ID correspondente
                    let existingItem = this.items.find(item => item.text === newVal || item.id === Utils.createSlug(newVal))
                    if (existingItem) {
                        this.itemId = existingItem.id
                    } else if (newVal) {
                        // Se não existe, cria um novo item temporário
                        let valueId = Utils.createSlug(newVal)
                        this.items.push({
                            text: newVal,
                            id: valueId
                        })
                        this.itemId = valueId
                    }
                } else {
                    this.itemId = null
                }
            }
        },
    }
};
</script>

